var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ChevronRightSmallIcon } from '@inter-ikea-kompis/icons';
import { CheckboxAlignButtonEnum } from '@inter-ikea-kompis/component-checkbox';
import { CheckboxLayoutEnum } from '@inter-ikea-kompis/component-checkbox';
import { Checkbox } from '@inter-ikea-kompis/component-checkbox';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { RadioLayoutEnum } from '@inter-ikea-kompis/component-radio-button';
import { RadioButton } from '@inter-ikea-kompis/component-radio-button';
import { Text } from '@inter-ikea-kompis/component-text';
import { Switch as SkapaSwitch } from '@ingka/switch-webc/passive.js';
import ActionListBorderEnum from '../../enums/ActionListBorderEnum.js';
import ActionListControlEnum from '../../enums/ActionListControlEnum.js';
import ActionListItemStyle from './ActionListItemStyle.js';
/**
 * Component that can render in an opened or closed state.
 *
 * @event select Triggered when action list or input control is clicked.
 */
export default class ActionListItem extends KompisElement {
    constructor() {
        super(...arguments);
        // Label
        this.label = null;
        // Caption
        this.caption = null;
        // Leading icon
        this.leadingIcon = null;
        // Leading Image
        this.imageSrc = null;
        // Horizontal padding
        this.horizontalPadding = false;
        // Disabled
        this.disabled = false;
        // For the compact variant
        this.compact = false;
        // Controls
        this.control = null;
        // Border for the action list
        this.actionListBorder = ActionListBorderEnum.bottom;
        // Label of the checkbox or radio button control
        this.controlLabel = null;
        // Is "true" if a control is checked
        this.checked = false;
        // Selected index
        this.selectedIndex = null;
        this.hovered = false;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        return html `
			<style>
				${ActionListItemStyle.getStyle({
            horizontalPadding: this.horizontalPadding,
            theme: this.theme,
            disabled: this.disabled,
            compact: this.compact,
            hovered: this.hovered,
            checked: this.checked,
            actionListBorder: this.actionListBorder
        })}
			</style>

			<button
				@click="${this.onClick.bind(this)}"
				@mouseover="${this.onMouseOver.bind(this)}"
				@mouseleave="${this.onMouseOut.bind(this)}"
				?disabled="${this.disabled}"
				tabindex="${this.control === ActionListControlEnum.chevron || this.control === null
            ? '0'
            : '-1'}"
			>
				<div class="container">
					${this.getIcon()} ${this.getImage()}
					<div class="textContainer">
						<kompis-text
							class="header"
							.theme="${this.theme}"
							.type="${ThemeFontStyleTypeEnum.bodyMedium}"
							.bold="${true}"
							>${this.label}</kompis-text
						>
						${this.getCaption()}
					</div>
				</div>
				${this.getControl()}
				<div class="outline"></div>
			</button>
			<slot></slot>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-switch', SkapaSwitch);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-checkbox', Checkbox);
        this.customElements.define('kompis-radio-button', RadioButton);
        this.customElements.define('kompis-text', Text);
        super.connectedCallback();
    }
    onMouseOver() {
        if (this.hovered) {
            return;
        }
        this.hovered = true;
        this.requestUpdate();
    }
    onMouseOut() {
        if (!this.hovered) {
            return;
        }
        this.hovered = false;
        this.requestUpdate();
    }
    /**
     * Returns the caption if provided.
     */
    getCaption() {
        if (!this.caption) {
            return null;
        }
        return html `
			<kompis-text
				class="caption"
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.bodyMedium}"
				>${this.caption}</kompis-text
			>
		`;
    }
    /**
     * Returns an icon if provided.
     */
    getIcon() {
        if (!this.leadingIcon) {
            return null;
        }
        else if (!this.imageSrc) {
            return html `
				<kompis-icon
					.theme="${this.theme}"
					.icon="${this.leadingIcon}"
					class="leadingIcon"
				></kompis-icon>
				<div class="spacing"></div>
			`;
        }
        else {
            return null;
        }
    }
    /**
     * Returns an image if provided.
     */
    getImage() {
        if (!this.imageSrc) {
            return null;
        }
        else if (!this.leadingIcon) {
            return html `<img alt="${this.label}" src="${this.imageSrc}" />
				<div class="spacing"></div>`;
        }
        else {
            return null;
        }
    }
    getControl() {
        switch (this.control) {
            case ActionListControlEnum.checkbox:
                return html `<kompis-checkbox
					@change="${(event) => this.onClick(event)}"
					.checked="${this.checked}"
					.disabled="${this.disabled}"
					.label="${this.controlLabel ? String(this.controlLabel) : null}"
					.alignButton="${CheckboxAlignButtonEnum.right}"
					.layout="${CheckboxLayoutEnum.subtle}"
					.hovered="${this.hovered}"
					.theme="${this.theme}"
				></kompis-checkbox>`;
            case ActionListControlEnum.chevron:
                return html `
					<kompis-icon
						class="icon"
						tabindex="-1"
						.flipIconRtl="${true}"
						.icon="${ChevronRightSmallIcon}"
						.theme="${this.theme}"
					></kompis-icon>
				`;
            case ActionListControlEnum.radioButton:
                return html `
					<kompis-radio-button
						.checked="${this.checked}"
						.disabled="${this.disabled}"
						.label="${this.controlLabel ? String(this.controlLabel) : null}"
						.alignButton="${CheckboxAlignButtonEnum.right}"
						.layout="${RadioLayoutEnum.subtle}"
						.hovered="${this.hovered}"
						.theme="${this.theme}"
					></kompis-radio-button>
				`;
            case ActionListControlEnum.switch:
                return html `
					<skapa-switch
						subtle
						.checked="${this.checked}"
						.disabled="${this.disabled}"
						auto-id="${`switch-${this.controlLabel}`}"
					>
						<input @change="${(event) => this.onClick(event)}" />
					</skapa-switch>
				`;
            default:
                return null;
        }
    }
    /**
     * Called when radio button checked value has changed.
     *
     * @param event Event data.
     */
    onClick(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.control === ActionListControlEnum.radioButton && this.checked) {
            return;
        }
        const enumsToCheck = [
            ActionListControlEnum.checkbox,
            ActionListControlEnum.radioButton,
            ActionListControlEnum.switch
        ];
        this.dispatchEvent(new CustomEvent('select', {
            bubbles: true,
            detail: {
                checked: this.control && enumsToCheck.includes(this.control) ? this.checked : null,
                selectedIndex: this.selectedIndex
            }
        }));
    }
}
__decorate([
    property({ type: Object, required: true })
], ActionListItem.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "label", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "caption", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "leadingIcon", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "imageSrc", void 0);
__decorate([
    property({ type: Boolean })
], ActionListItem.prototype, "horizontalPadding", void 0);
__decorate([
    property({ type: Boolean })
], ActionListItem.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], ActionListItem.prototype, "compact", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "control", void 0);
__decorate([
    property({ type: String })
], ActionListItem.prototype, "actionListBorder", void 0);
__decorate([
    property({ type: Number })
], ActionListItem.prototype, "controlLabel", void 0);
__decorate([
    property({ type: Boolean })
], ActionListItem.prototype, "checked", void 0);
__decorate([
    property({ type: Number })
], ActionListItem.prototype, "selectedIndex", void 0);
//# sourceMappingURL=ActionListItem.js.map